import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import { countryCodes } from '../data/mockData';

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [sub, setSub] = useState(false);
  const [fd, setFd] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [er, setEr] = useState({ email: '', phone: '' });
  const ve = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const hc = (f, v) => { setFd(p => ({ ...p, [f]: v })); if (f === 'email' || f === 'phone') setEr(p => ({ ...p, [f]: '' })); };
  const hp = (v) => { const d = v.replace(/\D/g, ''); if (d.length <= 10) { hc('phone', d); if (d && d.length !== 10) setEr(p => ({ ...p, phone: '10 digits required' })); } };
  const hs = async (e) => {
    e.preventDefault();
    const ne = { email: '', phone: '' }; let h = false;
    if (!ve(fd.email)) { ne.email = 'Valid email required'; h = true; }
    if (fd.phone.length !== 10) { ne.phone = '10 digits required'; h = true; }
    if (h) { setEr(ne); return; }
    setSub(true);
    setTimeout(() => {
      toast({ title: "Consultation Requested", description: "A member of our team will contact you within 24 hours." });
      setSub(false); setFd({ name: '', email: '', countryCode: '+1', phone: '', description: '' }); setEr({ email: '', phone: '' }); onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-[860px] p-0 gap-0 overflow-hidden border border-[#E2E2F0] bg-white rounded-2xl">
        <DialogTitle className="sr-only">Request a Consultation</DialogTitle>
        <div className="grid md:grid-cols-5">
          {/* Left — Image Panel */}
          <div className="hidden md:flex md:col-span-2 relative bg-[#1E1B4B] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1564846824194-346b7871b855?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzYyNzg2OTZ8MA&ixlib=rb-4.1.0&q=85"
              alt="Professional document signing"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E1B4B] via-[#1E1B4B]/60 to-transparent" />
            <div className="relative z-10 p-8 flex flex-col justify-end h-full">
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display',serif" }}>
                Protect What<br />You've Built.
              </h3>
              <ul className="space-y-2.5 mt-4">
                {['Free consultation', '24-hour response', '98% approval rate'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#A78BFA]" />
                    <span className="text-sm text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — Form */}
          <div className="md:col-span-3 p-8">
            <div className="mb-6">
              <div className="w-10 h-1 bg-gradient-to-r from-[#6366F1] to-[#A855F7] rounded-full mb-4" />
              <h2 className="text-xl font-bold text-[#1E1B4B] mb-1" style={{ fontFamily: "'Playfair Display',serif" }}>Request a Consultation</h2>
              <p className="text-sm text-[#4B5563]">Confidential. No obligation. 24-hour response.</p>
            </div>
            <form onSubmit={hs} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="dn" className="text-[#1E1B4B] text-xs font-medium">Full Name *</Label>
                <Input id="dn" data-testid="dialog-name-input" placeholder="Your full name" value={fd.name} onChange={e => hc('name', e.target.value)} required className="h-10 bg-[#F8FAFC] border-[#E2E2F0] text-[#1E1B4B] placeholder:text-[#9CA3AF] focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10 rounded-xl text-sm" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="de" className="text-[#1E1B4B] text-xs font-medium">Email *</Label>
                <Input id="de" data-testid="dialog-email-input" type="email" placeholder="you@company.com" value={fd.email} onChange={e => hc('email', e.target.value)} onBlur={() => { if (fd.email && !ve(fd.email)) setEr(p => ({ ...p, email: 'Valid email required' })); }} required className={`h-10 bg-[#F8FAFC] text-[#1E1B4B] placeholder:text-[#9CA3AF] rounded-xl text-sm ${er.email ? 'border-red-500' : 'border-[#E2E2F0]'} focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10`} />
                {er.email && <p className="text-red-500 text-xs">{er.email}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="dp" className="text-[#1E1B4B] text-xs font-medium">Phone *</Label>
                <div className="flex gap-2">
                  <Select value={fd.countryCode} onValueChange={v => hc('countryCode', v)}>
                    <SelectTrigger data-testid="dialog-country-select" className="w-[88px] h-10 bg-[#F8FAFC] border-[#E2E2F0] text-[#1E1B4B] focus:border-[#6366F1] rounded-xl text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-white border-[#E2E2F0] rounded-xl">{countryCodes.map(i => (<SelectItem key={i.code} value={i.code}><span className="flex items-center gap-2"><span>{i.flag}</span><span>{i.code}</span></span></SelectItem>))}</SelectContent>
                  </Select>
                  <Input id="dp" data-testid="dialog-phone-input" type="tel" placeholder="1234567890" value={fd.phone} onChange={e => hp(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-[#F8FAFC] text-[#1E1B4B] placeholder:text-[#9CA3AF] rounded-xl text-sm ${er.phone ? 'border-red-500' : 'border-[#E2E2F0]'} focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10`} />
                </div>
                {er.phone && <p className="text-red-500 text-xs">{er.phone}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="dd" className="text-[#1E1B4B] text-xs font-medium">Brand Details</Label>
                <Textarea id="dd" data-testid="dialog-description-input" placeholder="Briefly describe your brand or trademark needs" rows={2} value={fd.description} onChange={e => hc('description', e.target.value)} className="bg-[#F8FAFC] border-[#E2E2F0] text-[#1E1B4B] placeholder:text-[#9CA3AF] focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10 resize-none rounded-xl text-sm" />
              </div>
              <Button type="submit" data-testid="dialog-submit-button" className="w-full btn-gradient text-white font-semibold h-11 text-sm rounded-xl group" disabled={sub}>
                {sub ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</> : <>Submit Request <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>}
              </Button>
              <p className="text-[11px] text-[#9CA3AF] text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
